// Broker dashboard: overview stats, listings management, and leads.
import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {brokerSessionStorage} from "./brokerSession.storage";
import {PROPERTY_TYPES} from "./brokerConstants";
import {
    getMyProperties, createMyProperty, updateMyProperty, deleteMyProperty,
    setMyPropertyVisibility, getMyLeads, getMyDashboardStats,
    uploadPropertyImage, uploadPropertyImages,
} from "./brokerDashboardService";
import "./BrokerRegistration.css";
import "./BrokerDashboard.css";

const EMPTY_FORM = {
    title: "", location: "", price: "", type: "apartment", bhk: "",
    bedrooms: "", bathrooms: "", sqft: "", carpetArea: "",
    furnishing: "", facing: "", description: "", amenitiesText: "",
};

const BrokerDashboard = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");
    const [stats, setStats] = useState(null);
    const [properties, setProperties] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState("");

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [mainImageFile, setMainImageFile] = useState(null);
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);

    const refreshAll = useCallback(async () => {
        const [statsRes, propsRes, leadsRes] = await Promise.all([
            getMyDashboardStats(), getMyProperties(), getMyLeads(),
        ]);
        setStats(statsRes);
        setProperties(propsRes);
        setLeads(leadsRes);
    }, []);

    useEffect(() => {
        if (!brokerSessionStorage.get()) {
            navigate("/broker/login");
            return;
        }
        (async () => {
            try {
                await refreshAll();
            } catch (err) {
                setPageError(err.message || "Failed to load dashboard.");
            } finally {
                setLoading(false);
            }
        })();
    }, [navigate, refreshAll]);

    const updateField = (field) => (e) =>
        setForm((prev) => ({...prev, [field]: e.target.value}));

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setMainImageFile(null);
        setGalleryFiles([]);
        setEditingId(null);
        setFormError("");
    };

    const openCreateForm = () => {
        resetForm();
        setShowForm(true);
    };

    const openEditForm = (property) => {
        setForm({
            title: property.title || "",
            location: property.location || "",
            price: property.priceRaw ?? "",
            type: property.type || "apartment",
            bhk: property.bhk || "",
            bedrooms: property.bedrooms ?? "",
            bathrooms: property.bathrooms ?? "",
            sqft: property.sqft ?? "",
            carpetArea: property.carpetArea || "",
            furnishing: property.furnishing || "",
            facing: property.facing || "",
            description: property.description || "",
            amenitiesText: (property.amenities || []).join(", "),
        });
        setMainImageFile(null);
        setGalleryFiles([]);
        setEditingId(property.id);
        setFormError("");
        setShowForm(true);
    };

    const buildPayload = () => ({
        title: form.title.trim(),
        location: form.location.trim(),
        price: Number(form.price),
        type: form.type,
        bhk: form.bhk || null,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        sqft: form.sqft ? Number(form.sqft) : null,
        carpetArea: form.carpetArea || null,
        furnishing: form.furnishing || null,
        facing: form.facing || null,
        description: form.description || null,
        amenities: form.amenitiesText
            ? form.amenitiesText.split(",").map((a) => a.trim()).filter(Boolean)
            : [],
    });

    const handleSubmitListing = async () => {
        if (!form.title.trim() || !form.location.trim() || !form.price) {
            setFormError("Title, location and price are required.");
            return;
        }
        setFormError("");
        setSaving(true);
        try {
            const payload = buildPayload();
            let saved = editingId
                ? await updateMyProperty(editingId, payload)
                : await createMyProperty(payload);

            if (mainImageFile || galleryFiles.length > 0) {
                let mainImages = saved.mainImages || [];
                let images = saved.images || [];

                if (mainImageFile) {
                    const url = await uploadPropertyImage(saved.id, mainImageFile);
                    mainImages = [url, ...mainImages.filter((u) => u !== url)];
                }
                if (galleryFiles.length > 0) {
                    const urls = await uploadPropertyImages(saved.id, galleryFiles);
                    images = [...images, ...urls];
                }
                saved = await updateMyProperty(saved.id, {...payload, mainImages, images});
            }

            setShowForm(false);
            resetForm();
            await refreshAll();
        } catch (err) {
            setFormError(err.message || "Failed to save listing.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (property) => {
        if (!window.confirm(`Delete "${property.title}"? This cannot be undone.`)) return;
        try {
            await deleteMyProperty(property.id);
            await refreshAll();
        } catch (err) {
            setPageError(err.message || "Failed to delete listing.");
        }
    };

    const handleToggleActive = async (property) => {
        try {
            await setMyPropertyVisibility(property.id, !property.active);
            await refreshAll();
        } catch (err) {
            setPageError(err.message || "Failed to update listing visibility.");
        }
    };

    if (loading) {
        return (
            <div className="broker-page">
                <div className="broker-header">
                    <h1 className="broker-header__title">Broker Dashboard</h1>
                </div>
                <div className="broker-card broker-dashboard-card">Loading…</div>
            </div>
        );
    }

    return (
        <div className="broker-page">
            <div className="broker-header">
                <h1 className="broker-header__title">Broker Dashboard</h1>
                <p className="broker-header__subtitle">Manage your listings and track leads</p>
            </div>

            <div className="broker-card broker-dashboard-card">
                {pageError && <p className="broker-otp__error">⚠ {pageError}</p>}

                <div className="broker-dash-tabs">
                    <button
                        className={`broker-dash-tab${tab === "overview" ? " broker-dash-tab--active" : ""}`}
                        onClick={() => setTab("overview")}
                    >Overview</button>
                    <button
                        className={`broker-dash-tab${tab === "listings" ? " broker-dash-tab--active" : ""}`}
                        onClick={() => setTab("listings")}
                    >Listings ({properties.length})</button>
                    <button
                        className={`broker-dash-tab${tab === "leads" ? " broker-dash-tab--active" : ""}`}
                        onClick={() => setTab("leads")}
                    >Leads ({leads.length})</button>
                </div>

                {tab === "overview" && stats && (
                    <div className="broker-dash-stats">
                        <div className="broker-dash-stat-card">
                            <span className="broker-dash-stat-value">{stats.totalListings}</span>
                            <span className="broker-dash-stat-label">Total Listings</span>
                        </div>
                        <div className="broker-dash-stat-card">
                            <span className="broker-dash-stat-value">{stats.activeListings}</span>
                            <span className="broker-dash-stat-label">Active Listings</span>
                        </div>
                        <div className="broker-dash-stat-card">
                            <span className="broker-dash-stat-value">{stats.totalLeads}</span>
                            <span className="broker-dash-stat-label">Total Leads</span>
                        </div>
                    </div>
                )}

                {tab === "listings" && (
                    <div className="broker-dash-section">
                        <div className="broker-dash-section__header">
                            <button className="broker-btn broker-btn--primary broker-dash-add-btn" onClick={openCreateForm}>
                                + Add Listing
                            </button>
                        </div>

                        {properties.length === 0 && (
                            <p className="broker-dash-empty">You haven't added any listings yet.</p>
                        )}

                        {properties.length > 0 && (
                            <div className="broker-dash-table-wrap">
                                <table className="broker-dash-table">
                                    <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Location</th>
                                        <th>Price</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {properties.map((p) => (
                                        <tr key={p.id}>
                                            <td>{p.title}</td>
                                            <td>{p.location}</td>
                                            <td>{p.price}</td>
                                            <td>
                                                <span className={`broker-dash-badge${p.active ? " broker-dash-badge--active" : ""}`}>
                                                    {p.active ? "Active" : "Hidden"}
                                                </span>
                                            </td>
                                            <td className="broker-dash-actions">
                                                <button className="broker-dash-link-btn" onClick={() => openEditForm(p)}>Edit</button>
                                                <button className="broker-dash-link-btn" onClick={() => handleToggleActive(p)}>
                                                    {p.active ? "Hide" : "Publish"}
                                                </button>
                                                <button className="broker-dash-link-btn broker-dash-link-btn--danger" onClick={() => handleDelete(p)}>
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {tab === "leads" && (
                    <div className="broker-dash-section">
                        {leads.length === 0 && (
                            <p className="broker-dash-empty">No leads yet. Leads appear here when customers book a tour for one of your listings.</p>
                        )}
                        {leads.length > 0 && (
                            <div className="broker-dash-table-wrap">
                                <table className="broker-dash-table">
                                    <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Customer</th>
                                        <th>Mobile</th>
                                        <th>Scheduled</th>
                                        <th>Status</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {leads.map((lead) => (
                                        <tr key={lead.id}>
                                            <td>{lead.propertyTitle || `#${lead.propertyId}`}</td>
                                            <td>{lead.customerName}</td>
                                            <td>{lead.customerMobile}</td>
                                            <td>{lead.scheduledAt ? new Date(lead.scheduledAt).toLocaleString() : "—"}</td>
                                            <td>
                                                <span className="broker-dash-badge">{lead.status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showForm && (
                <div className="broker-dash-modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="broker-card broker-dash-form-card" onClick={(e) => e.stopPropagation()}>
                        <h2 className="broker-card__section-title">{editingId ? "Edit Listing" : "Add Listing"}</h2>

                        {formError && <p className="broker-otp__error">⚠ {formError}</p>}

                        <div className="broker-field">
                            <div className="broker-field__inner">
                                <div className="broker-field__label-row">
                                    <label className="broker-field__label">Title<span className="broker-field__required">*</span></label>
                                </div>
                                <div className="broker-field__control">
                                    <input className="broker-input" value={form.title} onChange={updateField("title")}/>
                                </div>
                            </div>
                        </div>

                        <div className="broker-field">
                            <div className="broker-field__inner">
                                <div className="broker-field__label-row">
                                    <label className="broker-field__label">Location<span className="broker-field__required">*</span></label>
                                </div>
                                <div className="broker-field__control">
                                    <input className="broker-input" value={form.location} onChange={updateField("location")}/>
                                </div>
                            </div>
                        </div>

                        <div className="broker-dash-form-row">
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Price (₹)<span className="broker-field__required">*</span></label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input className="broker-input" type="number" min="0" value={form.price} onChange={updateField("price")}/>
                                    </div>
                                </div>
                            </div>

                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Property Type</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <select className="broker-input" value={form.type} onChange={updateField("type")}>
                                            {PROPERTY_TYPES.map((t) => (
                                                <option key={t.id} value={t.id}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="broker-dash-form-row">
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Bedrooms</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input className="broker-input" type="number" min="0" value={form.bedrooms} onChange={updateField("bedrooms")}/>
                                    </div>
                                </div>
                            </div>
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Bathrooms</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input className="broker-input" type="number" min="0" value={form.bathrooms} onChange={updateField("bathrooms")}/>
                                    </div>
                                </div>
                            </div>
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Sqft</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input className="broker-input" type="number" min="0" value={form.sqft} onChange={updateField("sqft")}/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="broker-dash-form-row">
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Furnishing</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input className="broker-input" placeholder="e.g. Semi-Furnished"
                                               value={form.furnishing} onChange={updateField("furnishing")}/>
                                    </div>
                                </div>
                            </div>
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Facing</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input className="broker-input" placeholder="e.g. East"
                                               value={form.facing} onChange={updateField("facing")}/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="broker-field">
                            <div className="broker-field__inner">
                                <div className="broker-field__label-row">
                                    <label className="broker-field__label">Amenities</label>
                                </div>
                                <div className="broker-field__control">
                                    <input className="broker-input" placeholder="Comma-separated, e.g. Gym, Pool, Parking"
                                           value={form.amenitiesText} onChange={updateField("amenitiesText")}/>
                                </div>
                            </div>
                        </div>

                        <div className="broker-field">
                            <div className="broker-field__inner">
                                <div className="broker-field__label-row">
                                    <label className="broker-field__label">Description</label>
                                </div>
                                <div className="broker-field__control">
                                    <textarea className="broker-input" rows={3}
                                              value={form.description} onChange={updateField("description")}/>
                                </div>
                            </div>
                        </div>

                        <div className="broker-dash-form-row">
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Cover Image</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input type="file" accept="image/*"
                                               onChange={(e) => setMainImageFile(e.target.files[0] || null)}/>
                                    </div>
                                </div>
                            </div>
                            <div className="broker-field">
                                <div className="broker-field__inner">
                                    <div className="broker-field__label-row">
                                        <label className="broker-field__label">Gallery Images</label>
                                    </div>
                                    <div className="broker-field__control">
                                        <input type="file" accept="image/*" multiple
                                               onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="broker-btn-group">
                            <button className="broker-btn broker-btn--primary" onClick={handleSubmitListing} disabled={saving}>
                                {saving ? "Saving…" : editingId ? "Save Changes" : "Create Listing"}
                            </button>
                            <button className="broker-dash-cancel-btn" onClick={() => { setShowForm(false); resetForm(); }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrokerDashboard;
